import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, FormGrid, Field, TextInput, NumberInput, SelectInput } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, money, num } from '../../lib/format';
import type { Operation } from '../../lib/types';

// —— 工序详情（只读展示；编辑既有工序需管理员，后端 /api/records 限管理员）——
function OperationDetail({ row }: { row: Operation }) {
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.code}</span>
        <h3>{row.name_zh}</h3>
        {row.name_es ? <span className="muted">· {row.name_es}</span> : null}
        {row.qc_required ? <Pill tone="warn" label="需质检" /> : <Pill tone="neutral" label="免检" />}
      </div>
      <div className="kv-grid">
        <KeyValue k="工序编码">{row.code}</KeyValue>
        <KeyValue k="中文名称">{row.name_zh}</KeyValue>
        <KeyValue k="西语名称">{row.name_es || '—'}</KeyValue>
        <KeyValue k="工作站">{row.workstation || '—'}</KeyValue>
        <KeyValue k="默认工时(分)">{num(row.default_minutes, 0)}</KeyValue>
        <KeyValue k="小时成本">{money(row.hourly_cost, 'USD')}</KeyValue>
        <KeyValue k="质检要求">{row.qc_required ? '需要' : '不需要'}</KeyValue>
        <KeyValue k="ERPNext 编号">{row.legacy_erpnext_id || '—'}</KeyValue>
        <KeyValue k="更新时间">{dateLabel(row.updated_at)}</KeyValue>
      </div>
    </div>
  );
}

function NewOperationForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [f, setF] = useState<Record<string, string>>({ qc_required: '1', default_minutes: '0', hourly_cost: '0' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!f.code?.trim()) { setErr('工序编码为必填'); return; }
    if (!f.name_zh?.trim()) { setErr('中文名称为必填'); return; }
    setBusy(true);
    try {
      await api.post('/api/operations', {
        code: f.code.trim(),
        name_zh: f.name_zh.trim(),
        name_es: f.name_es || null,
        workstation: f.workstation || null,
        default_minutes: Number(f.default_minutes || 0),
        hourly_cost: Number(f.hourly_cost || 0),
        qc_required: f.qc_required === '1',
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>新建工序</h2><div className="desc">写入核心服务 /api/operations · 工单下达时按 BOM 工序展开为工序任务</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="工序编码" required><TextInput value={f.code ?? ''} onChange={(e) => set('code', e.target.value)} placeholder="如 OP-CUT" /></Field>
          <Field label="中文名称" required><TextInput value={f.name_zh ?? ''} onChange={(e) => set('name_zh', e.target.value)} /></Field>
          <Field label="西语名称"><TextInput value={f.name_es ?? ''} onChange={(e) => set('name_es', e.target.value)} /></Field>
          <Field label="工作站"><TextInput value={f.workstation ?? ''} onChange={(e) => set('workstation', e.target.value)} /></Field>
          <Field label="默认工时(分钟)"><NumberInput value={f.default_minutes ?? ''} onChange={(e) => set('default_minutes', e.target.value)} /></Field>
          <Field label="小时成本(USD)"><NumberInput value={f.hourly_cost ?? ''} onChange={(e) => set('hourly_cost', e.target.value)} /></Field>
          <Field label="质检要求">
            <SelectInput value={f.qc_required} onChange={(e) => set('qc_required', e.target.value)}>
              <option value="1">需要质检</option>
              <option value="0">免检</option>
            </SelectInput>
          </Field>
        </FormGrid>
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建工序'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function OperationsPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'operations', 'create');

  const columns: Column<Operation>[] = [
    { header: '工序编码', render: (o) => <span className="num strong">{o.code}</span> },
    { header: '中文名称', render: (o) => o.name_zh },
    { header: '西语名称', render: (o) => <span className="muted">{o.name_es || '—'}</span> },
    { header: '工作站', render: (o) => o.workstation || '—' },
    { header: '工时(分)', align: 'right', render: (o) => <span className="num">{num(o.default_minutes, 0)}</span> },
    { header: '时薪', align: 'right', render: (o) => <span className="num">{money(o.hourly_cost, 'USD')}</span> },
    { header: '质检', render: (o) => o.qc_required ? <Pill tone="warn" label="需质检" /> : <span className="muted">免检</span> },
  ];

  return (
    <>
      {creating ? (
        <NewOperationForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setReloadSignal((n) => n + 1); }} />
      ) : null}
      <ListPage<Operation>
        title="工序"
        desc="工序主数据 · /api/operations，供 BOM 工艺路线与工单工序任务引用"
        endpoint="/api/operations"
        columns={columns}
        rowId={(o) => o.id}
        showYear={false}
        searchPlaceholder="搜索工序编码 / 中文名 / 西语名…"
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="operations" row={row} fields={RECORD_FIELDS['operations']} label={String(row.name_zh)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建工序'}</button>
        ) : undefined}
        renderDetail={(row) => <OperationDetail row={row} />}
      />
    </>
  );
}
